/**
 * Authentication Types
 *
 * Defines the authentication and authorization model for the ITSM system.
 * Separates concerns between:
 * - User identity (who you are)
 * - Tech profile (IT staff assignment data)
 * - Session state (authentication status)
 */

import type { UserRole, UserStatus, Location, TechTier, Region, JobTag, TechWorkStatus, TicketType } from './ticket';
import { IT_ONLY_TICKET_TYPES, TicketType as TicketTypeValues } from './ticket';
import type { Department } from './knowledgeBase';

// ============================================
// USER TYPE DISCRIMINATOR
// ============================================

/**
 * Discriminator for user types in the system.
 * - END_USER: Regular employees who submit tickets
 * - IT_STAFF: IT technicians who work on tickets
 */
export const UserType = {
  END_USER: 'end_user',
  IT_STAFF: 'it_staff',
} as const;
export type UserType = (typeof UserType)[keyof typeof UserType];

// ============================================
// AUTHENTICATED USER
// ============================================

/**
 * Base user identity shared by all users.
 */
interface BaseUserIdentity {
  /** Unique user identifier */
  id: string;
  /** User's email address */
  email: string;
  /** Display name */
  name: string;
  /** User's department */
  department: Department;
  /** Physical location */
  location?: Location;
  /** Employee ID/Number */
  employeeId?: string;
  /** Corporate Directory System ID (Active Directory) */
  cdsid?: string;
}

/**
 * End user - regular employee who submits tickets.
 */
export interface EndUser extends BaseUserIdentity {
  userType: typeof UserType.END_USER;
}

/**
 * IT Staff user - technician who works on tickets.
 * Includes tech-specific assignment data.
 */
export interface ITStaffUser extends BaseUserIdentity {
  userType: typeof UserType.IT_STAFF;
  /** User role (Admin, T1 Tech, T2 Tech) */
  role: UserRole;
  /** Tech ID for assignment system (may differ from user ID) */
  techId: string;
  /** Support tier level */
  tier: TechTier;
  /** Geographic region */
  region: Region;
  /** Technical skills for routing */
  skills: JobTag[];
  /** Current ticket load */
  currentLoad: number;
  /** Maximum ticket capacity */
  maxLoad: number;
  /** Whether available for new assignments */
  isAvailable: boolean;
  /** Current work status */
  workStatus: TechWorkStatus;
}

/**
 * Union type for any authenticated user.
 */
export type AuthenticatedUser = EndUser | ITStaffUser;

// ============================================
// AUTH STATE
// ============================================

/**
 * Authentication state for the application.
 */
export interface AuthState {
  /** Whether auth has been checked/initialized */
  isInitialized: boolean;
  /** Whether there's an authenticated user */
  isAuthenticated: boolean;
  /** Whether auth check is in progress */
  isLoading: boolean;
  /** Current authenticated user (null if not authenticated) */
  user: AuthenticatedUser | null;
  /** Auth error message if any */
  error: string | null;
}

/**
 * Initial auth state before initialization.
 */
export const INITIAL_AUTH_STATE: AuthState = {
  isInitialized: false,
  isAuthenticated: false,
  isLoading: true,
  user: null,
  error: null,
};

// ============================================
// TYPE GUARDS
// ============================================

/**
 * Check if user is an IT staff member.
 */
export function isITStaff(user: AuthenticatedUser | null): user is ITStaffUser {
  return user !== null && user.userType === UserType.IT_STAFF;
}

/**
 * Check if user is an end user (non-IT).
 */
export function isEndUser(user: AuthenticatedUser | null): user is EndUser {
  return user !== null && user.userType === UserType.END_USER;
}

/**
 * Check if user has admin role.
 */
export function isAdmin(user: AuthenticatedUser | null): boolean {
  return isITStaff(user) && user.role === 'Admin';
}

/**
 * Check if user is T2 tech (senior technician).
 */
export function isT2Tech(user: AuthenticatedUser | null): boolean {
  return isITStaff(user) && (user.role === 'T2 Tech' || user.role === 'Admin');
}

/**
 * Check if user can access IT portal.
 */
export function canAccessITPortal(user: AuthenticatedUser | null): boolean {
  return isITStaff(user);
}

/**
 * Check if user can manage knowledge base articles.
 */
export function canManageKnowledgeBase(user: AuthenticatedUser | null): boolean {
  return isITStaff(user) && (user.role === 'Admin' || user.role === 'T2 Tech');
}

/**
 * Check if user can view all tickets (not just their own).
 */
export function canViewAllTickets(user: AuthenticatedUser | null): boolean {
  return isITStaff(user);
}

/**
 * Check if user can be assigned tickets.
 */
export function canBeAssignedTickets(user: AuthenticatedUser | null): boolean {
  return isITStaff(user) && user.isAvailable;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get the appropriate user ID for ticket requester/creator fields.
 * Always returns the user ID (not tech ID).
 */
export function getUserIdForTicket(user: AuthenticatedUser): string {
  return user.id;
}

/**
 * Get the tech ID for ticket assignment fields.
 * Only valid for IT staff users.
 */
export function getTechIdForAssignment(user: AuthenticatedUser): string | null {
  if (isITStaff(user)) {
    return user.techId;
  }
  return null;
}

/**
 * Get display name for user.
 */
export function getUserDisplayName(user: AuthenticatedUser | null): string {
  return user?.name ?? 'Unknown User';
}

/**
 * Get role display string.
 */
export function getUserRoleDisplay(user: AuthenticatedUser | null): string {
  if (!user) return 'Guest';
  if (isITStaff(user)) return user.role;
  return 'User';
}

// ============================================
// TICKET OWNERSHIP VALIDATION
// ============================================

/**
 * Ticket ownership info needed for validation.
 * Using a minimal interface to avoid circular dependencies.
 */
interface TicketOwnershipInfo {
  requesterId: string;
  createdBy?: string;
  assignedTechId: string | null;
}

/**
 * Check if a user can view a specific ticket.
 *
 * Rules:
 * - End users can view tickets where they are the requester
 * - IT staff can view all tickets
 * - Admins can view all tickets
 */
export function canViewTicket(user: AuthenticatedUser | null, ticket: TicketOwnershipInfo): boolean {
  if (!user) return false;

  // IT staff can view all tickets
  if (isITStaff(user)) {
    return true;
  }

  // End users can only view tickets they requested
  return ticket.requesterId === user.id;
}

/**
 * Check if a user can modify a specific ticket.
 *
 * Rules:
 * - End users can only add comments to their own tickets
 * - Assigned tech can fully modify the ticket
 * - T2 techs can modify any ticket in their assignment group
 * - Admins can modify any ticket
 */
export function canModifyTicket(user: AuthenticatedUser | null, ticket: TicketOwnershipInfo): boolean {
  if (!user) return false;

  // Admins can modify any ticket
  if (isAdmin(user)) {
    return true;
  }

  // IT staff checks
  if (isITStaff(user)) {
    // Assigned tech can modify
    if (ticket.assignedTechId === user.techId) {
      return true;
    }
    // T2 techs can modify any ticket
    if (isT2Tech(user)) {
      return true;
    }
    // T1 techs can modify unassigned tickets
    if (!ticket.assignedTechId) {
      return true;
    }
    return false;
  }

  // End users cannot modify tickets (only add comments)
  return false;
}

/**
 * Check if a user can add comments to a ticket.
 *
 * Rules:
 * - End users can comment on tickets they requested
 * - IT staff can comment on any ticket
 */
export function canCommentOnTicket(user: AuthenticatedUser | null, ticket: TicketOwnershipInfo): boolean {
  if (!user) return false;

  // IT staff can comment on any ticket
  if (isITStaff(user)) {
    return true;
  }

  // End users can comment on their own tickets
  return ticket.requesterId === user.id;
}

/**
 * Check if a user owns a ticket (is the requester).
 */
export function isTicketOwner(user: AuthenticatedUser | null, ticket: TicketOwnershipInfo): boolean {
  if (!user) return false;
  return ticket.requesterId === user.id;
}

/**
 * Check if a user is the assigned tech for a ticket.
 */
export function isAssignedToTicket(user: AuthenticatedUser | null, ticket: TicketOwnershipInfo): boolean {
  if (!user || !isITStaff(user)) return false;
  return ticket.assignedTechId === user.techId;
}

// ============================================
// TICKET TYPE PERMISSIONS
// ============================================

/**
 * Check if a user can create a specific ticket type.
 *
 * Rules:
 * - End users can only create Incident and Request tickets
 * - IT staff can create all ticket types (Incident, Request, Problem, Change)
 */
export function canCreateTicketType(user: AuthenticatedUser | null, ticketType: TicketType): boolean {
  if (!user) return false;

  // IT staff can create any ticket type
  if (isITStaff(user)) {
    return true;
  }

  // End users can only create non-IT-only ticket types
  return !IT_ONLY_TICKET_TYPES.includes(ticketType);
}

/**
 * Get list of ticket types available for a user to create.
 *
 * Rules:
 * - End users: Incident, Request
 * - IT staff: Incident, Request, Problem, Change
 */
export function getAvailableTicketTypes(user: AuthenticatedUser | null): TicketType[] {
  const allTypes = Object.values(TicketTypeValues);

  if (!user) {
    return [];
  }

  // IT staff can create all types
  if (isITStaff(user)) {
    return allTypes;
  }

  // End users can only create non-IT-only types
  return allTypes.filter((type) => !IT_ONLY_TICKET_TYPES.includes(type));
}

/**
 * Check if a ticket type is IT-only.
 */
export function isITOnlyTicketType(ticketType: TicketType): boolean {
  return IT_ONLY_TICKET_TYPES.includes(ticketType);
}
